import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useRef, useEffect } from 'react';
import { churches } from '../../../mocks/churches';
import { events } from '../../../mocks/events';

export default function Features() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Worship tools data
  const worshipTools = [
    { name: 'Hymnal', description: 'Complete SDA Hymnal with lyrics and audio', category: 'Worship Resources' },
    { name: 'Bible Study Guides', description: 'Quarterly lessons and study materials', category: 'Worship Resources' },
    { name: 'Sabbath School', description: 'Weekly Sabbath School lessons and resources', category: 'Worship Resources' },
    { name: 'Prayer Resources', description: 'Prayer guides and devotional materials', category: 'Worship Resources' },
    { name: 'Sermon Library', description: 'Access recorded sermons and messages', category: 'Worship Resources' },
    { name: 'Music Library', description: 'Worship music and praise songs', category: 'Worship Resources' },
  ];

  // Ministries data - includes both church and private ministries
  const ministries = [
    // Church Ministries
    { name: 'Youth Ministry', description: 'Empowering young people to grow in faith and leadership', category: 'Church Ministries' },
    { name: 'Women\'s Ministry', description: 'Supporting and empowering women in their spiritual journey', category: 'Church Ministries' },
    { name: 'Men\'s Ministry', description: 'Building strong men of faith and character', category: 'Church Ministries' },
    { name: 'Children\'s Ministry', description: 'Nurturing young hearts to love Jesus', category: 'Church Ministries' },
    { name: 'Health Ministry', description: 'Promoting wholistic health and wellness', category: 'Church Ministries' },
    { name: 'Music Ministry', description: 'Worshiping God through music and song', category: 'Church Ministries' },
    { name: 'Family Ministry', description: 'Strengthening families through Christ-centered programs', category: 'Church Ministries' },
    { name: 'Community Services', description: 'Serving our community with Christ\'s love', category: 'Church Ministries' },
    
    // Private Ministries & Groups
    { name: 'ATAN', description: 'Adventist Technology Association of Nigeria - Tech organization building tailored platforms and software', category: 'Private Ministries' },
    { name: 'Axios', description: 'Leading contemporary worship music ministry bringing fresh sounds to Adventist worship', category: 'Private Ministries' },
    { name: 'Axios Worship', description: 'Contemporary worship music ministry with Yoruba and English songs', category: 'Private Ministries' },
    { name: 'ENACTUS Babcock', description: 'Social Entrepreneurship & Community Development student-led initiative', category: 'Private Ministries' },
    { name: 'TMI', description: 'Total Member Involvement - Evangelism & Mission Training for personal outreach', category: 'Private Ministries' },
    { name: 'AV_Radio', description: 'Digital Radio Broadcasting streaming Adventist sermons and music 24/7', category: 'Private Ministries' },
  ];

  // Sermons data
  const sermons = [
    { title: 'The Power of Prayer in Modern Times', speaker: 'Pastor Emmanuel Adebayo', ministry: 'Youth Ministry', category: 'Sermons' },
    { title: 'Ìgbàgbọ́ àti Ìrètí: Ọ̀nà sí Àlàáfíà', speaker: 'Elder Adewale Johnson', ministry: 'Men\'s Ministry', category: 'Sermons' },
    { title: 'Ƙarfin Addini a Rayuwar Matasa', speaker: 'Sister Hauwa Ibrahim', ministry: 'Women\'s Ministry', category: 'Sermons' },
  ];

  // Media content
  const mediaContent = [
    { title: 'The Great Controversy', type: 'Documentary', description: 'Epic biblical documentary on spiritual warfare', category: 'Movies' },
    { title: 'Ìjìnlẹ̀ Ọ̀rọ̀ Ìyìn', type: 'Drama', description: 'Nigerian Christian drama with Yoruba cultural elements', category: 'Movies' },
    { title: 'Worthy is the Lamb', artist: 'SDA Nigeria Choir', description: 'Worship song by SDA Nigeria Choir', category: 'Music' },
    { title: 'Ọlọ́run Mi Tóbi', artist: 'Axios Worship', description: 'Contemporary worship by Axios in Yoruba', category: 'Music' },
  ];

  // Resources Hub data - comprehensive list
  const resourcesHubContent = [
    // Study & Devotion
    { name: 'Adult Sabbath School Bible Study Guide', description: 'Quarterly Bible study lessons for adult Sabbath School classes', category: 'Study & Devotion', type: 'PDF' },
    { name: 'Youth Sabbath School Study Guide', description: 'Engaging Bible study materials designed for youth', category: 'Study & Devotion', type: 'PDF' },
    { name: 'Children\'s Sabbath School Materials', description: 'Age-appropriate Bible study resources for children', category: 'Study & Devotion', type: 'PDF' },
    { name: 'Adult Daily Devotional', description: 'Daily spiritual reflections and Bible readings', category: 'Study & Devotion', type: 'Web' },
    { name: 'Youth Daily Devotional', description: 'Inspiring daily devotions for young people', category: 'Study & Devotion', type: 'Web' },
    { name: 'Family Worship Resources', description: 'Materials to strengthen family devotional time', category: 'Study & Devotion', type: 'PDF' },
    { name: 'Children\'s Devotional Materials', description: 'Age-appropriate devotional content for children', category: 'Study & Devotion', type: 'PDF' },
    { name: 'Mission Quarterly', description: 'Stories from the mission field around the world', category: 'Study & Devotion', type: 'PDF' },
    { name: 'Children\'s Mission Stories', description: 'Mission stories adapted for children', category: 'Study & Devotion', type: 'PDF' },
    { name: 'Week of Prayer Readings', description: 'Official Week of Prayer devotional materials', category: 'Study & Devotion', type: 'PDF' },
    { name: 'Bible Reading Plans', description: 'Guided plans for reading through the Bible', category: 'Study & Devotion', type: 'PDF' },
    { name: 'Spiritual Growth Resources', description: 'Materials for deepening your walk with God', category: 'Study & Devotion', type: 'Web' },
    
    // Music & Worship
    { name: 'SDA Hymnal - PDF', description: 'Complete Seventh-day Adventist Hymnal in PDF format', category: 'Music & Worship', type: 'PDF' },
    { name: 'SDA Hymnal - PowerPoint Slides', description: 'Hymnal lyrics formatted for worship presentations', category: 'Music & Worship', type: 'PPTX' },
    { name: 'SDA Hymnal - EasyWorship Format', description: 'Hymnal database for EasyWorship presentation software', category: 'Music & Worship', type: 'Software' },
    { name: 'SDA Hymnal - OpenLP Format', description: 'Hymnal database for OpenLP presentation software', category: 'Music & Worship', type: 'Software' },
    { name: 'SDA Hymnal - OBS Format', description: 'Hymnal overlay for OBS streaming software', category: 'Music & Worship', type: 'Software' },
    { name: 'Responsive Readings Collection', description: 'Scripture readings for congregational worship', category: 'Music & Worship', type: 'PDF' },
    { name: 'Calls to Worship', description: 'Opening worship invitations and liturgies', category: 'Music & Worship', type: 'PDF' },
    { name: 'Worship Service Order Templates', description: 'Customizable templates for worship planning', category: 'Music & Worship', type: 'PDF' },
    { name: 'Choir Music Library', description: 'Collection of approved choir arrangements', category: 'Music & Worship', type: 'PDF' },
    
    // Stewardship
    { name: 'Titan Stewardship System', description: 'Official digital giving platform for SDA churches', category: 'Stewardship', type: 'Web' },
    { name: '7me Giving Platform', description: 'Mobile and web-based church giving solution', category: 'Stewardship', type: 'Web' },
    { name: 'Conference Giving Portal', description: 'Conference-approved online giving system', category: 'Stewardship', type: 'Web' },
    { name: 'Stewardship Manual', description: 'Comprehensive guide to biblical stewardship principles', category: 'Stewardship', type: 'PDF' },
    { name: 'Offering Promotion Toolkit', description: 'Materials for promoting church offerings', category: 'Stewardship', type: 'PDF' },
    { name: 'Financial Peace Resources', description: 'Biblical principles for financial wellness', category: 'Stewardship', type: 'PDF' },
    
    // Leadership
    { name: 'Church Manual', description: 'Official guide for church governance and operations', category: 'Leadership', type: 'PDF' },
    { name: 'Elders\' Manual', description: 'Comprehensive guide for church elders', category: 'Leadership', type: 'PDF' },
    { name: 'Pastors\' Manual', description: 'Resource guide for pastoral ministry', category: 'Leadership', type: 'PDF' },
    { name: 'Deacons & Deaconesses Manual', description: 'Guidelines for deacon and deaconess ministry', category: 'Leadership', type: 'PDF' },
    { name: 'Youth Ministries Manual', description: 'Guidelines for youth ministry leadership', category: 'Leadership', type: 'PDF' },
    { name: 'Music Ministries Manual', description: 'Standards and practices for church music', category: 'Leadership', type: 'PDF' },
    { name: 'Children\'s Ministries Manual', description: 'Guide for children\'s ministry programs', category: 'Leadership', type: 'PDF' },
    { name: 'Women\'s Ministries Manual', description: 'Resources for women\'s ministry leadership', category: 'Leadership', type: 'PDF' },
    { name: 'Men\'s Ministries Manual', description: 'Guidelines for men\'s ministry programs', category: 'Leadership', type: 'PDF' },
    { name: 'Sabbath School & Personal Ministries Manual', description: 'Comprehensive guide for Sabbath School leadership', category: 'Leadership', type: 'PDF' },
    
    // Policy
    { name: 'Working Policy', description: 'General Conference Working Policy documents', category: 'Policy', type: 'PDF' },
    { name: 'Official Statements & Guidelines', description: 'Church positions on contemporary issues', category: 'Policy', type: 'Web' },
    { name: 'Division Constitution & By-Laws', description: 'Division-level organizational documents', category: 'Policy', type: 'PDF' },
    { name: 'Union Constitution & By-Laws', description: 'Union-level organizational documents', category: 'Policy', type: 'PDF' },
    { name: 'Division Circulars & Notices', description: 'Official communications from Division leadership', category: 'Policy', type: 'PDF' },
    { name: 'Union Policy Documents', description: 'Union-level governance and policy materials', category: 'Policy', type: 'PDF' },
    { name: 'Conference Guidelines', description: 'Conference-specific policies and procedures', category: 'Policy', type: 'PDF' },
    { name: 'Child Protection Policy', description: 'Guidelines for safeguarding children in church programs', category: 'Policy', type: 'PDF' },
    { name: 'Safeguarding Guidelines', description: 'Comprehensive safeguarding policies and procedures', category: 'Policy', type: 'PDF' },
    
    // Bible Apps
    { name: 'YouVersion Bible App', description: 'Popular Bible app with multiple translations', category: 'Bible Apps', type: 'App' },
    { name: 'Bible Gateway', description: 'Comprehensive online Bible study platform', category: 'Bible Apps', type: 'Web' },
    { name: 'Blue Letter Bible', description: 'In-depth Bible study tools and resources', category: 'Bible Apps', type: 'Web' },
    { name: 'Yorùbá Bible App', description: 'Dedicated Yorùbá language Bible application', category: 'Bible Apps', type: 'App' },
    { name: 'Igbo Bible App', description: 'Bible in Igbo language with audio', category: 'Bible Apps', type: 'App' },
    { name: 'Hausa Bible App', description: 'Complete Hausa Bible with study tools', category: 'Bible Apps', type: 'App' },
    { name: 'Nigerian Pidgin Bible', description: 'Bible in Nigerian Pidgin English', category: 'Bible Apps', type: 'Web' },
    
    // Digital Systems
    { name: 'Church Membership System', description: 'Official platform for membership records and management', category: 'Digital Systems', type: 'Web' },
    { name: 'Adventist Education Portal', description: 'Access to SDA educational resources and systems', category: 'Digital Systems', type: 'Web' },
    { name: 'Adventist Publishing Network', description: 'Official church literature and publishing resources', category: 'Digital Systems', type: 'Web' },
    { name: 'Health Ministries Portal', description: 'Resources for health and wellness ministry', category: 'Digital Systems', type: 'Web' },
    { name: 'Adventist News Network', description: 'Official news service of the Seventh-day Adventist Church', category: 'Digital Systems', type: 'Web' },
    { name: 'Hope Channel', description: 'Official television network of the Seventh-day Adventist Church', category: 'Digital Systems', type: 'Web' },
    
    // Media & Identity
    { name: 'Global Church Logo Package', description: 'Official Seventh-day Adventist Church logos', category: 'Media & Identity', type: 'ZIP' },
    { name: 'Division Logo Package', description: 'Division-specific logos and branding', category: 'Media & Identity', type: 'ZIP' },
    { name: 'Brand Identity Guidelines', description: 'Official guidelines for church branding and identity', category: 'Media & Identity', type: 'PDF' },
    { name: 'Evangelism Media Toolkit', description: 'Graphics and media for evangelistic programs', category: 'Media & Identity', type: 'ZIP' },
    { name: 'Communication Toolkit', description: 'Templates and resources for church communications', category: 'Media & Identity', type: 'ZIP' },
  ];

  // Search across all content
  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return null;

    const query = searchQuery.toLowerCase().trim();
    const results = {
      churches: churches.filter(church => 
        church.name.toLowerCase().includes(query) ||
        church.location.toLowerCase().includes(query) ||
        church.state.toLowerCase().includes(query) ||
        church.region.toLowerCase().includes(query) ||
        church.conference.toLowerCase().includes(query)
      ).slice(0, 3),
      
      events: events.filter(event =>
        event.title.toLowerCase().includes(query) ||
        event.description.toLowerCase().includes(query) ||
        event.location.toLowerCase().includes(query) ||
        event.category.toLowerCase().includes(query)
      ).slice(0, 3),
      
      tools: worshipTools.filter(tool =>
        tool.name.toLowerCase().includes(query) ||
        tool.description.toLowerCase().includes(query)
      ).slice(0, 3),
      
      ministries: ministries.filter(ministry =>
        ministry.name.toLowerCase().includes(query) ||
        ministry.description.toLowerCase().includes(query) ||
        ministry.category.toLowerCase().includes(query)
      ).slice(0, 4),

      sermons: sermons.filter(sermon =>
        sermon.title.toLowerCase().includes(query) ||
        sermon.speaker.toLowerCase().includes(query) ||
        sermon.ministry.toLowerCase().includes(query)
      ).slice(0, 3),

      media: mediaContent.filter(media =>
        media.title.toLowerCase().includes(query) ||
        media.description.toLowerCase().includes(query) ||
        (media.artist && media.artist.toLowerCase().includes(query)) ||
        (media.type && media.type.toLowerCase().includes(query))
      ).slice(0, 3),

      resources: resourcesHubContent.filter(resource =>
        resource.name.toLowerCase().includes(query) ||
        resource.description.toLowerCase().includes(query) ||
        resource.category.toLowerCase().includes(query) ||
        resource.type.toLowerCase().includes(query)
      ).slice(0, 5),
    };

    const hasResults = results.churches.length > 0 || results.events.length > 0 || 
                      results.tools.length > 0 || results.ministries.length > 0 ||
                      results.sermons.length > 0 || results.media.length > 0 ||
                      results.resources.length > 0;

    return hasResults ? results : null;
  }, [searchQuery]);

  const handleSearchFocus = () => {
    if (searchQuery.length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setShowSuggestions(value.length >= 2);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setShowSuggestions(false);
  };

  const features = [
    {
      icon: 'ri-live-line',
      title: t('watchLive'),
      description: t('watchLiveDesc'),
      color: 'from-rose-500 to-pink-600',
      link: '/live'
    },
    {
      icon: 'ri-map-pin-line',
      title: t('findChurch'),
      description: t('findChurchDesc'),
      color: 'from-emerald-500 to-teal-600',
      link: '/directory'
    },
    {
      icon: 'ri-calendar-event-line',
      title: t('events'),
      description: t('eventsDesc'),
      color: 'from-blue-500 to-indigo-600',
      link: '/events'
    },
    {
      icon: 'ri-team-line',
      title: t('ministries'),
      description: t('ministriesDesc'),
      color: 'from-purple-500 to-violet-600',
      link: '/ministries'
    },
  ];

  return (
    <div className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
            {t('whatYouCanDo')}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            {t('platformDescription')}
          </p>
        </div>

        {/* Global Search Bar */}
        <div className="max-w-3xl mx-auto mb-12 sm:mb-16 px-4" ref={searchRef}>
          <div className="relative">
            <div className="relative">
              <input
                type="text"
                placeholder="Search churches, events, ministries, sermons, music, resources, and more..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={handleSearchFocus}
                className="w-full px-6 py-4 pl-14 pr-12 rounded-2xl text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-xl border border-gray-200 bg-white"
              />
              <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-emerald-600 text-2xl"></i>
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer p-1"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              )}
            </div>

            {/* Search Suggestions Dropdown */}
            {showSuggestions && searchResults && (
              <div className="absolute top-full left-0 right-0 mt-3 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50 max-h-[600px] overflow-y-auto">
                {/* Resources Hub */}
                {searchResults.resources.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Resources Hub</div>
                      <Link
                        to="/resources-hub"
                        onClick={() => setShowSuggestions(false)}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold cursor-pointer"
                      >
                        View All
                      </Link>
                    </div>
                    {searchResults.resources.map((resource, idx) => (
                      <Link
                        key={idx}
                        to="/resources-hub"
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-indigo-100 flex-shrink-0">
                          <i className="ri-book-open-line text-indigo-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{resource.name}</div>
                          <div className="text-xs text-gray-600 truncate">{resource.description}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500">{resource.category}</span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-500">{resource.type}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Churches */}
                {searchResults.churches.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Churches</div>
                      <Link
                        to="/directory"
                        onClick={() => setShowSuggestions(false)}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold cursor-pointer"
                      >
                        View All
                      </Link>
                    </div>
                    {searchResults.churches.map((church, idx) => (
                      <Link
                        key={idx}
                        to={`/church/${church.id}`}
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-100 flex-shrink-0">
                          <i className="ri-building-line text-emerald-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{church.name}</div>
                          <div className="text-xs text-gray-600 truncate">{church.location}, {church.state}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Events */}
                {searchResults.events.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Events</div>
                      <Link
                        to="/events"
                        onClick={() => setShowSuggestions(false)}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold cursor-pointer"
                      >
                        View All
                      </Link>
                    </div>
                    {searchResults.events.map((event, idx) => (
                      <Link
                        key={idx}
                        to="/events"
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-100 flex-shrink-0">
                          <i className="ri-calendar-event-line text-blue-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{event.title}</div>
                          <div className="text-xs text-gray-600 truncate">{event.location} • {event.date}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Ministries */}
                {searchResults.ministries.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Ministries & Groups</div>
                      <Link
                        to="/ministries"
                        onClick={() => setShowSuggestions(false)}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold cursor-pointer"
                      >
                        View All
                      </Link>
                    </div>
                    {searchResults.ministries.map((ministry, idx) => (
                      <Link
                        key={idx}
                        to="/ministries"
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-rose-100 flex-shrink-0">
                          <i className="ri-team-line text-rose-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{ministry.name}</div>
                          <div className="text-xs text-gray-600 truncate">{ministry.description}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{ministry.category}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Sermons */}
                {searchResults.sermons.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Sermons & Messages</div>
                      <Link
                        to="/ministries"
                        onClick={() => setShowSuggestions(false)}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold cursor-pointer"
                      >
                        View All
                      </Link>
                    </div>
                    {searchResults.sermons.map((sermon, idx) => (
                      <Link
                        key={idx}
                        to="/ministries"
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-indigo-100 flex-shrink-0">
                          <i className="ri-mic-line text-indigo-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{sermon.title}</div>
                          <div className="text-xs text-gray-600 truncate">{sermon.speaker} • {sermon.ministry}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Media (Movies & Music) */}
                {searchResults.media.length > 0 && (
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Media Library</div>
                      <Link
                        to="/ministries"
                        onClick={() => setShowSuggestions(false)}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold cursor-pointer"
                      >
                        View All
                      </Link>
                    </div>
                    {searchResults.media.map((media, idx) => (
                      <Link
                        key={idx}
                        to="/ministries"
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-purple-100 flex-shrink-0">
                          <i className={`${media.category === 'Music' ? 'ri-music-line' : 'ri-film-line'} text-purple-600 text-lg`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{media.title}</div>
                          <div className="text-xs text-gray-600 truncate">
                            {media.artist || media.type} • {media.category}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Worship Tools */}
                {searchResults.tools.length > 0 && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Worship Resources</div>
                      <Link
                        to="/tools"
                        onClick={() => setShowSuggestions(false)}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold cursor-pointer"
                      >
                        View All
                      </Link>
                    </div>
                    {searchResults.tools.map((tool, idx) => (
                      <Link
                        key={idx}
                        to="/tools"
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-100 flex-shrink-0">
                          <i className="ri-book-open-line text-teal-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">{tool.name}</div>
                          <div className="text-xs text-gray-600 truncate">{tool.description}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* No Results */}
            {showSuggestions && searchQuery.length >= 2 && !searchResults && (
              <div className="absolute top-full left-0 right-0 mt-3 bg-white rounded-2xl shadow-2xl border border-gray-200 p-8 text-center z-50">
                <i className="ri-search-line text-4xl text-gray-300 mb-3"></i>
                <p className="text-sm text-gray-600">No results found for "{searchQuery}"</p>
                <p className="text-xs text-gray-500 mt-2">Try different keywords or browse our sections below</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <Link
              key={index}
              to={feature.link}
              className="group relative bg-white rounded-xl lg:rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-transparent cursor-pointer overflow-hidden"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
              
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center rounded-xl bg-emerald-600 mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className={`${feature.icon} text-2xl sm:text-3xl text-white`}></i>
              </div>
              
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                {feature.title}
              </h3>
              
              <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 leading-relaxed">
                {feature.description}
              </p>
              
              <div className="flex items-center text-teal-600 font-semibold group-hover:translate-x-2 transition-transform duration-300">
                <span className="whitespace-nowrap text-sm sm:text-base">{t('learnMore')}</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
